import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { CellRenderer } from '@shared/interfaces/table/cell-renderer';
import { StatusConfig } from '@shared/components/table/cells/status-cell/status-config';
import { NgClass } from '@angular/common';
import { TooltipDirective } from '@shared/directives/tooltip/tooltip.directive';

/**
 * Component responsible for rendering status indicators in table cells.
 * Implements the CellRenderer interface for table integration.
 *
 * This component is primarily used through table column configurations to display
 * status values with customizable labels and colors. It supports both string and
 * boolean status values, with boolean values being automatically converted to '0'/'1'.
 *
 * ```typescript
 * {
 *   title: 'Status',
 *   key: 'status',
 *   cellRenderer: StatusCellComponent,
 *   cellRendererParams: {
 *     'active': { label: 'Active', color: 'green-500' },
 *     'inactive': { label: 'Inactive', color: 'red-500' },
 *     '1': { label: 'Enabled', color: 'green-500' },
 *     '0': { label: 'Disabled', color: 'red-500' }
 *   }
 * }
 * ```
 */
@Component({
  selector: 'app-status-cell',
  imports: [
    TranslatePipe,
    TooltipDirective,
    NgClass,
  ],
  templateUrl: './status-cell.component.html',
  styleUrl: './status-cell.component.css'
})
export class StatusCellComponent extends CellRenderer implements OnInit, OnChanges {
  /**
   * The color to be applied to the status indicator.
   * Set during initialization based on the matched status configuration.
   */
  public color?: string;

  /**
   * Default status configurations that can be overridden by cellRendererParams.
   * Provides fallback configurations for common status values.
   */
  public config: Record<string, StatusConfig> = {};

  public ngOnInit(): void {
    this.updateStatus();
  }

  /**
   * Recalculate when input-bound values (from table/cell renderer) change.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if ('value' in changes || 'data' in changes || 'params' in changes || 'config' in changes) {
      this.updateStatus();
    }
  }

  /**
   * Shared logic to compute label and color from current value.
   * Safe to call multiple times as data changes.
   */
  private updateStatus(): void {
    const key = this.params?.['key'];
    const rawValue = (key && this.data && key in this.data) ? this.data[key] : this.value;

    // Reset if no value
    this.color = undefined;

    let normalizedValue =
      rawValue !== undefined && rawValue !== null
        ? String(rawValue).toLowerCase()
        : undefined;

    /** Convert boolean values to numerical strings */
    if (typeof rawValue === 'boolean') {
      normalizedValue = rawValue ? '1' : '0';
    }

    if (!normalizedValue) {
      return;
    }

    const rendererParams: Record<string, StatusConfig> =
      this.params?.cellRendererParams ?? {};

    const statusConfig: StatusConfig =
      rendererParams[normalizedValue] ?? this.config[normalizedValue];

    if (statusConfig) {
      this.value = statusConfig.label;
      this.color = statusConfig.color;
    } else {
      this.value = String(rawValue);
      this.color = undefined;
    }
  }
}
